import React, { useLayoutEffect, useRef, useEffect, useState } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import FullScreenIcon from '../../../features/common/FullScreenIcon/FullScreenIcon';
import DownloadChart from '../../../components/time-series-chart/download/downloadChart';
import { exportChart } from '../../../utilities/ExportChart';
import EmptyState from '../../../components/empty-state/EmptyState';

interface ChartData {
  category: string;
  value: number;
  color: string;
}

interface XAxisLabelStyle {
  fontSize?: number;
  color?: string;
  fontFamily?: string;
  fontWeight?:
    | '400'
    | 'normal'
    | 'bold'
    | 'bolder'
    | 'lighter'
    | '100'
    | '200'
    | '300'
    | '500'
    | '600'
    | '700'
    | '800'
    | '900';
  lineHeight?: number;
}

interface ChartProps {
  title: string;
  subtitle?: string;
  xAxisLabel?: string;
  yAxisLabel?: string;
  data: ChartData[];
  tooltipFormat?: string;
  showActionButtons?: boolean;
  width?: string;
  height?: string;
  chartHeight?: string;
  xAxisLabelStyle?: XAxisLabelStyle;
  margin?: string;
}

const BarChart: React.FC<ChartProps> = ({
  title,
  subtitle,
  xAxisLabel,
  yAxisLabel,
  data,
  tooltipFormat = '{categoryX}: {valueY}',
  showActionButtons = true,
  width = 'w-full',
  chartHeight = 'h-[190px]',
  xAxisLabelStyle,
  margin,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Check if data is empty or all values are zero
  const hasData = data && data.length > 0 && data.some((item) => item.value > 0);

  const chartId = `bar-chart-content-${title.replace(/\s+/g, '').toLowerCase()}`;

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const toggleFullscreen = () => {
    if (!containerRef.current) return;

    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch((err) => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      document.exitFullscreen();
    }
  };

  const handleChartDownload = async (option: string) => {
    const element = document.getElementById(chartId);
    const filename = `${title.replace(/\s+/g, '')}-BarChart`;
    if (!element) return;
    await exportChart(element, getExportType(option), filename, undefined, undefined, title);
  };

  const getExportType = (option: string): 'JPG' | 'PDF' => (option === 'Download as PDF' ? 'PDF' : 'JPG');

  useLayoutEffect(() => {
    if (!chartRef.current || !hasData) return;

    // Create root element
    const root = am5.Root.new(chartRef.current);

    // Set themes
    root.setThemes([am5themes_Animated.new(root)]);

    // Create chart
    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: 'none',
        wheelY: 'none',
      }),
    );

    // Create axes
    const xRenderer = am5xy.AxisRendererX.new(root, {
      minGridDistance: 30,
      minorGridEnabled: true,
    });

    xRenderer.labels.template.setAll({
      fontSize: xAxisLabelStyle?.fontSize ?? 12,
      fill: am5.color(xAxisLabelStyle?.color ?? '#F7F9F9'),
      fontFamily: xAxisLabelStyle?.fontFamily ?? 'Mulish',
      fontWeight: xAxisLabelStyle?.fontWeight ?? '400',
      lineHeight: xAxisLabelStyle?.lineHeight ?? 1.5, // 150% as a ratio (18px/12px = 1.5)
    });

    xRenderer.grid.template.setAll({
      strokeOpacity: 0,
      stroke: am5.color('#4a5463'),
      //strokeDasharray: [2, 2],
    });

    const xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: 'category',
        renderer: xRenderer,
        tooltip: am5.Tooltip.new(root, {}),
      }),
    );

    // X-axis title
    if (xAxisLabel) {
      xAxis.children.push(
        am5.Label.new(root, {
          text: xAxisLabel,
          x: am5.p50,
          centerX: am5.p50,
          y: am5.p100,
          dy: -20,
          fontSize: 12,
          fill: am5.color('#F7F9F9'),
          fontWeight: '400',
        }),
      );
    }

    // // Configure axis line visibility through renderer stroke
    // xRenderer.set('stroke', am5.color('#4a5463'));
    // xRenderer.set('strokeOpacity', 0.8);

    const yRenderer = am5xy.AxisRendererY.new(root, {
      strokeOpacity: 0,
    });

    yRenderer.labels.template.setAll({
      fontSize: 12,
      fill: am5.color('#f7f9f9'),
      fontFamily: 'Mulish',
    });

    yRenderer.grid.template.setAll({
      strokeOpacity: 1,
      stroke: am5.color('#4a5463'),
      strokeDasharray: [5, 5],
    });

    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        min: 0,
        max: Math.max(...data.map((d) => d.value)) * 1.05,
        renderer: yRenderer,
      }),
    );

    // Y-axis title
    if (yAxisLabel) {
      yAxis.children.push(
        am5.Label.new(root, {
          text: yAxisLabel,
          rotation: -90,
          y: am5.p50,
          centerY: am5.p50,
          x: am5.p0,
          centerX: am5.p50,
          dx: -12,
          fontSize: 12,
          fill: am5.color('#F7F9F9'),
          fontWeight: '400',
        }),
      );
    }

    // Add single series
    const series = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: title,
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'value',
        categoryXField: 'category',
        tooltip: am5.Tooltip.new(root, {
          labelText: tooltipFormat,
        }),
      }),
    );

    series.columns.template.setAll({
      width: am5.percent(60),
      strokeWidth: 0,
    });

    // value label bullet on top of each bar
    series.bullets.push((root, _series, dataItem) => {
      const value = dataItem?.get('valueY');
      return am5.Bullet.new(root, {
        locationY: 1,
        sprite: am5.Label.new(root, {
          text: value !== undefined ? value.toString() : '',
          fill: am5.color('#F7F9F9'),
          fontSize: 12,
          fontWeight: '400',
          centerX: am5.p50,
          centerY: am5.p100,
          dy: 6,
        }),
      });
    });

    // Set individual colors for each bar
    series.columns.template.adapters.add('fill', (fill, target) => {
      const dataItem = target.dataItem;
      if (dataItem) {
        const color = (dataItem.dataContext as ChartData)?.color;
        return color ? am5.color(color) : am5.color('#3b82f6');
      }
      return fill;
    });

    xAxis.data.setAll(data);
    series.data.setAll(data);

    // Remove legend for single value bars

    // Make stuff animate on load
    chart.appear(1000, 100);

    return () => {
      root.dispose();
    };
  }, [data, tooltipFormat, title, xAxisLabelStyle, hasData]);

  const renderActionButtons = () => {
    if (!showActionButtons) return null;

    return (
      <>
        <button
          className='bg-none border-none cursor-pointer p-1 rounded flex items-center justify-center transition-colors hover:bg-white hover:bg-opacity-10'
          onClick={toggleFullscreen}
          analytics-id='bar-chart-fullscreen-btn'
        >
          <FullScreenIcon />
        </button>
        <button
          className='bg-none border-none cursor-pointer p-1 rounded flex items-center justify-center transition-colors hover:bg-white hover:bg-opacity-10'
          analytics-id='bar-chart-download-btn'
        >
          <DownloadChart onClick={(option) => handleChartDownload(option)} canExportJpg={true} canExportPdf={true} />
        </button>
      </>
    );
  };

  return (
    <div
      ref={containerRef}
      className={`recent-tests-widget esp-bar-chart-container ${isFullscreen ? 'fixed top-0 left-0 w-screen h-screen z-[9999] m-0 p-5 rounded-none' : ''}`}
      style={{ margin: margin ? margin : '6px 8px' }}
    >
      <div className='widget-content'>
        <div className={`${width} `}>
          <div className='widget-header' style={{ padding: '25px 22px 16px' }}>
            <div className='flex flex-col gap-[8px] items-start grow'>
              <span className='section-title'>{title}</span>
            </div>
            {renderActionButtons()}
          </div>
          <div style={{ padding: '10px 22px 2px' }}>
            <div className='divider'></div>
          </div>
          <div id={chartId}>
            {hasData ? (
              <>
                {subtitle && (
                  <div className='text-center esp-bar-chart-subtitle'>
                    <span className='section-subtitle'>{subtitle}</span>
                  </div>
                )}
                <div className='flex items-center'>
                  <div
                    ref={chartRef}
                    className={`flex-1 ${isFullscreen ? '' : chartHeight}`}
                    style={isFullscreen ? { width: '100vw', height: '80vh', minHeight: '600px' } : {}}
                  />
                </div>
              </>
            ) : (
              <EmptyState message='No data available to display in bar chart' />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BarChart;
